'use client';

/**
 * Single-row toolbar for the /recipes page: live search + filters + sort.
 *
 * Each child reads/writes its own slice of the URL (SearchForm → `q`,
 * RecipeFilters → `dietary` / `cuisine` / etc., SortDropdown → `sort`). The
 * wrapper exists purely to own the row layout — children stay independent.
 */

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { SearchForm } from './search-form';
import { SortDropdown } from './sort-dropdown';
import { RecipeFiltersWithAuth } from './recipe-filters-with-auth';
import { Button } from '@/components/ui/button';
import styles from './recipes-toolbar.module.css';

interface TaxonomyItem {
  id: string;
  name: string;
}

interface RecipesToolbarProps {
  cuisines: TaxonomyItem[];
  categories: TaxonomyItem[];
}

export function RecipesToolbar({ cuisines, categories }: RecipesToolbarProps) {
  const t = useTranslations('recipes.browse');
  const searchParams = useSearchParams();
  const hasQuery = (searchParams.get('q')?.trim().length ?? 0) > 0;

  return (
    <div className={styles.toolbar} role="region" aria-label={t('toolbarAria')}>
      <SearchForm />
      <div className={styles.controls}>
        <RecipeFiltersWithAuth
          cuisines={cuisines}
          categories={categories}
          preserveParams={['q', 'sort', 'dir']}
        />
        <SortDropdown hasQuery={hasQuery} />
        {/* Persistent primary entry point to the create/generate flow. Shown to
            everyone; anonymous users bounce to login at /cookbook/create. */}
        <Button
          as={Link}
          href="/cookbook/create"
          variant="primary"
          leadingIcon="sparkles"
          className={styles.generate}
        >
          {t('generate')}
        </Button>
      </div>
    </div>
  );
}
